/**
 * Loads / unloads the Sensemakers customer-service chat widget (served by the
 * chatbot service). Env-gated on `VITE_ChatWidgetOrigin`, like the Google Tag
 * Manager loader in `config.ts`: nothing is added unless a chatbot origin is
 * configured.
 *
 * The widget is only shown to logged-in users (see `main.ts`), and is loaded with
 * the user's current language (`data-lang`) — which it uses for its own UI and the
 * chat replies. Re-authenticating in a different language reloads it accordingly.
 *
 * Auth: the widget fetches a short-lived JWT from `/api/chat/token` (Vite's `/api`
 * proxy / the production reverse proxy forwards it to the members API), then calls
 * the chatbot with that bearer token. So the members API must expose that route.
 * The chat "session" (a new conversation per login) is driven entirely by that
 * JWT's per-login `lsid` claim on the chatbot side — nothing is needed here.
 *
 * The widget's own floating launcher is suppressed (`data-launcher="host"`): the
 * chat is opened from the header icon (see `components/header`) via the global
 * the widget installs, and `data-anchor` makes its panel hang from that icon.
 *
 * The anchor must be resolved lazily, when the panel opens — it does not exist
 * while `widget.js` runs. The icon is rendered only once the widget has loaded,
 * so the element the selector names appears a tick after the script finishes. A
 * widget that resolved `data-anchor` once at init would get `null` and fall back
 * to the viewport corner, which is the placement this arrangement exists to fix.
 *
 * The chatbot is an out-of-hours channel, so it is only offered while customer
 * service is closed. The chatbot service owns that schedule and answers it at
 * `/api/chat/availability`; `chatWidgetLoaded` drives the header icon, so during
 * business hours neither the icon nor the widget itself is present.
 */
import { ref } from 'vue';

const SCRIPT_ID = 'sensemakers-chat-widget';
const LINK_ID = 'sensemakers-chat-widget-css';
const AVAILABILITY_TIMEOUT_MS = 5000;

export const chatWidgetLoaded = ref(false);

export async function isChatAvailable(origin: string): Promise<boolean> {
    if (!origin) {
        return false;
    }

    // Fail closed: if the chatbot cannot be reached there is no working chat to
    // offer, so the icon stays hidden until a later check succeeds. The timeout
    // makes that prompt as well — a black-holed host would otherwise leave one
    // pending request behind per poll — and `no-store` keeps a cacheable answer
    // from outliving the schedule it reports.
    //
    // `AbortController` rather than `AbortSignal.timeout()`: the latter needs
    // Safari 16, above this build's floor (no `browserslist`, no `build.target`,
    // so Vite's default `modules` target — Safari 14). It would throw into the
    // catch below, making the chat permanently invisible on an older device and
    // indistinguishable from an unreachable chatbot.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), AVAILABILITY_TIMEOUT_MS);

    try {
        const response = await fetch(`${origin.replace(/\/+$/, '')}/api/chat/availability`, {
            cache: 'no-store',
            signal: controller.signal,
        });

        return response.ok && (await response.json()).available === true;
    } catch {
        return false;
    } finally {
        clearTimeout(timeout);
    }
}

export function loadChatWidget(origin: string, lang: string): void {
    if (!origin || typeof document === 'undefined') {
        return;
    }

    const base = origin.replace(/\/+$/, '');
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
        // Already loaded — reload only if the language changed (so the widget UI +
        // chat replies follow it).
        if (existing.getAttribute('data-lang') === lang) {
            // The gate clears the flag while deliberately leaving the script in place, so
            // an open conversation outlives the business-hours boundary. Announcing from
            // the global rather than returning blind keeps "a visible icon implies a
            // working widget" honest without needing a fresh `onload` to get back to true.
            chatWidgetLoaded.value = !!window.SensemakersChat;
            return;
        }
        unloadChatWidget();
    }

    const link = document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'stylesheet';
    link.href = `${base}/widget.css`;
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `${base}/widget.js`;
    script.defer = true;
    // The widget reads these off its own <script> element (document.currentScript).
    script.setAttribute('data-api', base);
    script.setAttribute('data-token-url', '/api/chat/token');
    script.setAttribute('data-lang', lang);
    script.setAttribute('data-launcher', 'host');
    script.setAttribute('data-anchor', '.header__chat');
    // Only announce the widget once its script has actually run: the icon drives
    // the global the widget installs, so showing it any earlier means a click
    // that does nothing. A failed load unwinds instead of leaving a dead icon.
    //
    // `load` also fires for a script that was fetched and then threw while
    // initialising (a truncated asset, an HTML error page served as 200, a bug in
    // the widget's own bootstrap) — `error` covers only a failed fetch. So check
    // for the global the icon actually depends on rather than trusting the event,
    // and unwind otherwise, which also lets the next poll retry instead of
    // latching on a script element that is still in the DOM.
    script.onload = () => {
        if (window.SensemakersChat) {
            chatWidgetLoaded.value = true;
        } else {
            unloadChatWidget();
        }
    };
    script.onerror = () => {
        unloadChatWidget();
    };
    document.body.appendChild(script);
}

export function toggleChatWidget(): void {
    window.SensemakersChat?.toggle();
}

/**
 * Whether a conversation is on screen, so the gate can stop offering the chat
 * without destroying one in progress. Reads the widget's own open class, the same
 * coupling `unloadChatWidget()` already has on its `csbot-` prefix — the widget
 * exposes no open state of its own yet.
 */
export function isChatPanelOpen(): boolean {
    return typeof document !== 'undefined' && document.querySelector('.csbot-panel.csbot-open') !== null;
}

/**
 * Takes the widget's DOM away and drops our handle to it. This is a stopgap, not
 * a teardown: `widget.js` has already run by now, and the resize/focus/
 * visibilitychange listeners and revalidation interval it installed on
 * `window`/`document` survive both the node removal and the `delete`, so a
 * load → unload → load cycle re-executes it over the previous run's listeners
 * (it has no guard against a second run). Cleaning that up needs the widget to
 * expose a `destroy()` — see sensemakers-chatbot#1.
 */
export function unloadChatWidget(): void {
    if (typeof document === 'undefined') {
        return;
    }
    chatWidgetLoaded.value = false;
    document.getElementById(SCRIPT_ID)?.remove();
    document.getElementById(LINK_ID)?.remove();
    delete window.SensemakersChat;
    // The widget renders elements prefixed `csbot-` on <body>; remove them so the
    // panel disappears when the chat is no longer on offer.
    document.querySelectorAll('[class^="csbot-"], [class*=" csbot-"]').forEach((node) => node.remove());
}
